import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map, take } from 'rxjs';
import { User } from '../models/auth.interface';
import { AuthService } from '../services/auth.service';
import { isManagerDashboardEligibleRole } from '../models/user-role.types';

export const managerDashboardGuard: CanActivateFn = route => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const decide = (user: User) => {
    const hasEligibleRole = isManagerDashboardEligibleRole(user.role);
    const isTeamManager = (user.userTeamContext?.managedTeamIds?.length ?? 0) > 0;
    const hasDashboardCapability = user.effectiveCapabilities?.managerDashboard ?? authService.hasCapability('managerDashboard');
    const practiceOnly = user.effectiveCapabilities?.practiceOnlyManagerDashboard === true;

    if (route.routeConfig?.path === 'manager-dashboard' && practiceOnly) {
      return router.createUrlTree(['/main/practice-courses']);
    }

    if ((hasEligibleRole || isTeamManager) && user.organisationId && hasDashboardCapability) {
      return true;
    }
    return router.createUrlTree(['/main/dashboard']);
  };

  // After sign-in the user is always there. `toObservable` leaves an effect behind until the route's injector
  // is destroyed, so it is only for the navigations that run before the user loads.
  const user = authService.currentUser();
  if (user !== null) return decide(user);
  return toObservable(authService.currentUser).pipe(
    filter((next): next is User => next !== null),
    take(1),
    map(decide)
  );
};
